import { useState } from 'react'
import type { ReactNode } from 'react'
import { Alert, Button, CodeInput } from 'armxlib'
import { en } from '../../i18n/en'
import { useCaptcha } from '../../lib/turnstile'
import { failureReason } from './api'

const t = en.auth

/** Props of {@link CodeStep}. */
export interface CodeStepProps {
  /** Label of the submit button. */
  submitLabel: string
  /** Checks the code; throws an `AuthFailure` to show its reason. */
  onSubmit: (code: string) => Promise<void>
  /** Sends a new code, with a fresh Turnstile token. */
  onResend: (captchaToken: string) => Promise<void>
  /** Whether the other fields are ready; the code is not sent before. */
  canSubmit?: boolean
  /** Fields shown above the code, like the new password. */
  children?: ReactNode
}

/**
 * Second step of sign-up and password reset: the 6-digit code from the email,
 * sent as soon as the last digit is typed.
 */
export function CodeStep({ submitLabel, onSubmit, onResend, canSubmit = true, children }: CodeStepProps) {
  const [code, setCode] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resent, setResent] = useState(false)
  const captcha = useCaptcha()

  async function submit(value: string) {
    if (value.length < 6 || !canSubmit || pending) return
    setPending(true)
    setError(null)
    try {
      await onSubmit(value)
    } catch (failure) {
      setError(t.errors[failureReason(failure)])
      setCode('')
    } finally {
      setPending(false)
    }
  }

  async function resend() {
    if (!captcha.token) {
      setError(captcha.failed ? t.captchaFailed : t.captchaPending)
      return
    }
    setError(null)
    try {
      await onResend(captcha.token)
      setResent(true)
    } catch (failure) {
      setError(t.errors[failureReason(failure)])
    } finally {
      captcha.reset()
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        void submit(code)
      }}
    >
      {error && <Alert tone="danger">{error}</Alert>}
      {resent && !error && <Alert tone="success">{t.code.resent}</Alert>}
      {children}
      <CodeInput
        label={t.code.label}
        hint={t.code.hint}
        value={code}
        onChange={setCode}
        onComplete={(value) => void submit(value)}
        disabled={pending}
        autoFocus
      />
      <Button type="submit" variant="primary" fullWidth loading={pending} disabled={code.length < 6 || !canSubmit}>
        {submitLabel}
      </Button>
      {captcha.widget}
      <Button type="button" variant="ghost" fullWidth onClick={() => void resend()}>
        {t.code.resend}
      </Button>
    </form>
  )
}
